import type { DocWorkflowEntry } from '../../app/state';
import type { NodeHealthCounts } from '../../lib/health';
import type { DocId, DocType, NodeId } from '../../lib/types';
import type { SliceCreator } from '../state';

/**
 * Documentation: what pages exist and which one the overlay is showing.
 *
 * TWO DIFFERENT THINGS SHARE THIS SLICE, on purpose. The four maps are metadata about pages —
 * titles resolved server-side for the current locale, the URL that opens each one, the type of
 * the ones that are not `standard`, and which ones Claude wrote. The nine fields under them
 * are the doc VIEW's state: which page, in which mode, opened from which node.
 *
 * They belong together because every consumer of one needs the other in the same breath: the
 * bar renders `docCurrentTitle` and the navigation band renders pills built from `docTitles`,
 * and splitting them would mean two subscriptions to answer one question.
 *
 * NOTE ON `docTypes`: only the EXCEPTIONS are stored — an id that is absent is a `standard`
 * page. That is the shape the server sends, and inverting it here would be a second thing to
 * keep true.
 *
 * `docOpenSeq` and `docEditSessionId` look like plumbing and are not. Without the sequence,
 * re-opening the SAME page in the same mode changes nothing observable, so a revision restore
 * — which reloads the page already open — would do nothing at all. Without the session id, a
 * sitting cannot outlive one component, and the version timeline reads "edited by X" once per
 * Save click rather than once per person.
 */
export interface DocsSlice {
  /** Doc-page id -> title, resolved server-side for the current locale. */
  docTitles: Record<DocId, string>;
  /** Doc-page id -> the URL that opens it. Derived on load; never persisted. */
  docUrls: Record<DocId, string>;
  /** Only the pages that are NOT `standard`. Absent means written here. */
  docTypes: Record<DocId, DocType>;
  /** Doc-page id -> true when Claude wrote it. AI-admin only. */
  docAiGenerated: Record<DocId, boolean>;
  /**
   * Doc-page id -> its workflow standing at its RESOLVED locale (V0.38), sparse.
   *
   * A FIFTH CATALOGUE rather than a field on the four above, because it does not arrive with
   * them: `GET /api/map` has never carried per-page workflow state and must not start — the
   * automatic demotion happens inside a document save, which deliberately does not bump
   * `dataVersion`, so a state riding the map would be stale in exactly the case it matters.
   * It comes from `GET /api/docs/workflows` instead, and is patched in place by every
   * transition.
   *
   * It lives in the store as well as the snapshot because the heatmap lens (V0.39) colours
   * map nodes from it, and a map node reads the store rather than a snapshot.
   */
  docWorkflow: Record<DocId, DocWorkflowEntry>;
  /** "Nothing to clear" and "we have not asked" are different pictures on a heatmap. */
  docWorkflowLoaded: boolean;
  /**
   * Node id -> workflow counts from `GET /api/map/health` (V0.39), sparse — the clearance
   * heatmap's server half. In the store for the same reason `docWorkflow` is: the lens
   * colours map nodes, and a map node reads the store rather than a snapshot. The entries
   * are replaced wholesale by every load and never mutated, so identity comparison is exact.
   */
  mapHealth: Record<NodeId, NodeHealthCounts>;
  /** False until the fetch has SUCCEEDED — a failed fetch paints nothing rather than lying. */
  mapHealthLoaded: boolean;
  /**
   * Node id -> the to-dos done/total beneath a PROJECT node (V0.50.3), sparse — this reader's
   * own rollup of `GET /api/projects` (`loadProjectProgress()`), present only for a VIEW_TODOS
   * holder. In the store for `mapHealth`'s reason: the heatmap lens draws a project node's
   * progress bar from it, and a map node reads the store rather than a snapshot. Replaced whole
   * by every load and never mutated, so identity comparison per entry is exact.
   */
  projectProgress: Record<NodeId, { done: number; total: number }>;

  /** Whether the overlay is on screen at all. */
  docOpen: boolean;
  docCurrentId: DocId | null;
  /** The URL in the frame. Empty on a node that has no page to show. */
  docCurrent: string;
  /** What the bar says — set by whoever opened it, so it can name the slot it came from. */
  docCurrentTitle: string;
  /** The node the page was opened FROM — what the navigation band is centred on. */
  docNavNodeId: NodeId | null;
  /** Bumped on every open, so re-opening the same page in the same mode is observable. */
  docOpenSeq: number;
  /** One id per editing sitting, so repeated saves collapse into a single log entry. */
  docEditSessionId: string | null;
  /** Bumped whenever a save or a restore adds an entry to the current page's history. */
  docRevision: number;

  /** Wholesale, from `GET /api/map` — these four always arrive together. */
  setDocCatalogue: (next: {
    docTitles: Record<DocId, string>;
    docUrls: Record<DocId, string>;
    docTypes: Record<DocId, DocType>;
    docAiGenerated: Record<DocId, boolean>;
  }) => void;

  /**
   * The workflow catalogue, replaced whole. Content-compared like the four above, and for the
   * same reason: the mirror re-states it on every publish with a freshly spread container.
   */
  setDocWorkflow: (next: { docWorkflow: Record<DocId, DocWorkflowEntry>; docWorkflowLoaded: boolean }) => void;

  /** The health catalogue, replaced whole — `setDocWorkflow`'s contract exactly. */
  setMapHealth: (next: { mapHealth: Record<NodeId, NodeHealthCounts>; mapHealthLoaded: boolean }) => void;

  /** The project rollup, replaced whole — `setMapHealth`'s contract without the loaded flag. */
  setProjectProgress: (next: Record<NodeId, { done: number; total: number }>) => void;

  /** The whole doc-view state, which changes as one thing on every open/close/step. */
  setDocView: (next: {
    docOpen: boolean;
    docCurrentId: DocId | null;
    docCurrent: string;
    docCurrentTitle: string;
    docNavNodeId: NodeId | null;
    docOpenSeq: number;
    docEditSessionId: string | null;
    docRevision: number;
  }) => void;
}

/** Same keys, same values? The catalogue maps arrive as fresh spreads on every sync. */
function sameRecord<T>(a: Record<string, T>, b: Record<string, T>): boolean {
  if (a === b) return true;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
}

export const createDocsSlice: SliceCreator<DocsSlice> = (set, get) => ({
  docTitles: {},
  docUrls: {},
  docTypes: {},
  docAiGenerated: {},
  docWorkflow: {},
  docWorkflowLoaded: false,
  mapHealth: {},
  mapHealthLoaded: false,
  projectProgress: {},

  docOpen: false,
  docCurrentId: null,
  docCurrent: '',
  docCurrentTitle: '',
  docNavNodeId: null,
  docOpenSeq: 0,
  docEditSessionId: null,
  docRevision: 0,

  // Both compare by CONTENT before writing. `store/fromApp.ts` re-states them on every
  // sync with freshly spread containers, so identity always differs even when nothing did —
  // and each notification costs one selector evaluation per map node. See `slices/ui.ts`.
  setDocCatalogue: (next) => {
    const s = get();
    if (
      sameRecord(s.docTitles, next.docTitles) &&
      sameRecord(s.docUrls, next.docUrls) &&
      sameRecord(s.docTypes, next.docTypes) &&
      sameRecord(s.docAiGenerated, next.docAiGenerated)
    ) {
      return;
    }
    set(next);
  },

  // The entries are OBJECTS, so this compares them by identity — which is exact here because
  // nothing ever mutates one: `loadDocWorkflows()` builds them and every patch replaces one.
  setDocWorkflow: (next) => {
    const s = get();
    if (s.docWorkflowLoaded === next.docWorkflowLoaded && sameRecord(s.docWorkflow, next.docWorkflow)) return;
    set(next);
  },

  // Identity per entry again: `loadMapHealth()` replaces the whole container and every entry
  // object with it, and nothing mutates one in place.
  setMapHealth: (next) => {
    const s = get();
    if (s.mapHealthLoaded === next.mapHealthLoaded && sameRecord(s.mapHealth, next.mapHealth)) return;
    set(next);
  },

  // The same argument: `loadProjectProgress()` builds a fresh container and fresh entries, and
  // no writer mutates one in place. No `loaded` flag — an absent entry paints no bar, which is
  // the right picture both before the fetch and for a reader who may not see the to-dos.
  setProjectProgress: (next) => {
    if (sameRecord(get().projectProgress, next)) return;
    set({ projectProgress: next });
  },

  setDocView: (next) => {
    const s = get();
    if (
      s.docOpen === next.docOpen &&
      s.docCurrentId === next.docCurrentId &&
      s.docCurrent === next.docCurrent &&
      s.docCurrentTitle === next.docCurrentTitle &&
      s.docNavNodeId === next.docNavNodeId &&
      s.docOpenSeq === next.docOpenSeq &&
      s.docEditSessionId === next.docEditSessionId &&
      s.docRevision === next.docRevision
    ) {
      return;
    }
    set(next);
  },
});
